import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { setTag } from "@sentry/browser";
import SitumSDK, {
  type BuildingListElement,
  type Organization,
  type PoiCategory as SDKPoiCategory,
} from "@situm/sdk-js";

import {
  selectOnline,
  selectPoiCategories,
  setApiPermissionLevel,
  setCurrentOrganization,
  setError,
  setPoiCategories,
} from "../app/appSlice";
import {
  type BuildingElement,
  BuildingState,
  setBuildings,
  setCurrentBuildingID,
} from "@/features/cartography/cartographySlice";
import {
  selectCurrentBuildingID,
  selectCurrentBuildingState,
  selectStoreBuildings,
} from "@/features/cartography/cartographySlice.selectors";
import { selectConfigItem } from "../config/configSlice";
import { selectMapStyleLoaded } from "../map/mapSlice";
import { selectProfileOrganizationId } from "../session/sessionSlice";

import { useAppDispatch, useAppSelector, useBreakpoints } from "@/app/hooks";
import { API_SERVER_DOMAIN } from "@/constants";
import type { SitumFlightInformation } from "@/features/flight_information/types";
import {
  buildingMapper,
  createFavoritePoiCategory,
  organizationMapper,
  poiCategoryFactory,
  poiMapper,
} from "@/libs/factories";
import type IMDF from "@/libs/imdf/imdf";
import type IMDFTheme from "@/libs/imdf/theme";
import { JobQueue } from "@/libs/job";
import {
  type Building,
  type ModelConfig,
  type ModelScans,
  type PoiCategories,
  PoiCategory,
  type Pois,
} from "@/libs/models";
import type { Paths } from "@/libs/models/path";
import {
  mapActiveShareLocationResponse,
  mapSharedLocationSession,
} from "@/libs/share_location";
import { removeProfileFromUrlWithReload } from "@/libs/url";
import { CACHED_AT_HEADER_NAME, CARTOGRAPHY_CACHE_NAME } from "@/sw/constants";
import { FALLBACK_LANGUAGE } from "../app/constants";
import {
  appendBuildingsAction,
  applyBuildingMapper,
  getInitialBuilding,
  getStyleFromCF,
  loadIMDf,
  setBuildingAction,
  setBuildingsAction,
} from "../cartography/actions";
import { loadConfig } from "../config/actions";
import { PrefetchMode } from "../config/types";
import { SHARED_LOCATION_ENDPOINT } from "../share_location/constants";
import type {
  ActiveShareLocationResponse,
  SharedLocationSession,
  ShareLocationSession,
} from "../share_location/types";
import { dateToTimestamp } from "../utils/date";
import {
  emmitEventAppStarted,
  setEventEmitterJWTGetter,
} from "../utils/eventEmmiter";
import { safeLocalStorage } from "../utils/utils";

const DESKTOP_CONCURRENT_REQUEST_LIMIT = 20;
const DESKTOP_REQUEST_INTERVAL = 100;
const MOBILE_CONCURRENT_REQUEST_LIMIT = 15;
const MOBILE_REQUEST_INTERVAL = 500;
const LAST_MODIFIED_HEADER_NAME = "last-modified";

export type SitumAPIContextType = {
  jobQueue: JobQueue;
  // Situm API interface
  situmAPI: SitumSDK;
  isAuthenticated: boolean;
  getJWT?: () => string;
  getBuildingById: ({
    id,
    force,
  }: {
    id: number;
    force?: boolean;
  }) => Promise<Building | null> | undefined;
  getPoisOnBuilding: ({
    id,
  }: {
    id: number;
  }) => Promise<Pois | null> | undefined;
  setPoisOnBuilding({ id, pois }: { id: number; pois: Pois }): void;
  getBuildings: () => Promise<readonly BuildingListElement[]>;
  getPoiCategories: () => Promise<SDKPoiCategory[]>;
  getCurrentOrganization: () => Promise<Organization>;
  putPaths: (buildingId: number, paths: Paths) => Promise<void>;
  fetchAllBuildings: () => Promise<void>;
  // Internal API
  uploadGeoJSON: (args: { buildingID: number; geojson: IMDF }) => Promise<void>;
  deleteGeoJSON: (buildingID: number) => Promise<void>;
  uploadGeoJSONTheme: (args: {
    buildingID: number;
    geojsonTheme: IMDFTheme;
  }) => Promise<void>;
  getCalibrationData: (
    floorId: number,
    type: "GT" | "BLE" | "WIFI",
    signal?: AbortSignal,
  ) => Promise<ModelScans>;
  getModelConfig: (buildingId: number) => Promise<ModelConfig>;
  // Data loading
  initApp: (profileName: string) => Promise<void>;
  loadBuilding: (b: Building, force?: boolean) => Promise<void>;
  syncLoadBuilding: ({ id }: { id: Building["id"] }) => Promise<Building>;
  refreshData: ({
    searchParams,
    profileUrl,
    profileDomain,
  }: {
    searchParams: any;
    profileUrl: string | null;
    profileDomain: string | null;
  }) => Promise<void>;
  // Prefetch
  buildingsToPrefetch: Building[] | null | undefined;
  poiCategoriesToPrefetch: PoiCategories | null | undefined;
  prefetchBuildingIds: number[];
  setPrefetchBuildingIds: (x: number[]) => void;
  addPrefetchBuildingId: (id: number) => void;
  isBuildingPrefetched: (id: number) => boolean;
  // Utilities
  hasCurrentBuildingDataChanged: () => Promise<void>;

  uploadMapStyle: (file: File) => Promise<{ name: string; url: string } | null>;
  uploadIconImage: (
    file: File,
  ) => Promise<{ id: string | number; url: string } | null>;
  createShareLocationSession: ({
    deviceId,
    durationMinutes,
    signal,
  }: {
    deviceId: string;
    durationMinutes: number;
    signal?: AbortSignal;
  }) => Promise<ShareLocationSession>;
  deleteShareLocationSession: ({
    id,
    deviceId,
  }: {
    id: string;
    deviceId: string;
  }) => Promise<void>;
  getSharedLocationById: ({
    locationSessionId,
  }: {
    locationSessionId: string;
  }) => Promise<SharedLocationSession | null>;
  getActiveShareLocationSessionByDeviceId: ({
    deviceId,
  }: {
    deviceId: string;
  }) => Promise<ActiveShareLocationResponse | null>;
  getFlightsInformation: () => Promise<SitumFlightInformation[]>;
};

export const SitumAPIContext = createContext<SitumAPIContextType | null>(null);

export const useSitumAPIContext = () => {
  const value = useContext(SitumAPIContext);
  if (!value) {
    throw new Error(
      "useSitumAPIContext must be used within a <SitumAPIProvider/>",
    );
  }
  return value;
};

export const SitumAPIProvider = ({ children }: { children: ReactNode }) => {
  // Redux
  const dispatch = useAppDispatch();
  const apikey = useAppSelector(selectConfigItem("auth.api_key"));
  const jwt = useAppSelector(selectConfigItem("auth.jwt"));
  const domain = useAppSelector(selectConfigItem("auth.domain"));
  const storeBuildings = useAppSelector(selectStoreBuildings);
  const poiCategories = useAppSelector(selectPoiCategories);
  const mapStyleLoaded = useAppSelector(selectMapStyleLoaded);
  const isOnline = useAppSelector(selectOnline);
  const prefetchData = useAppSelector(
    selectConfigItem("internal.prefetchData"),
  );
  const currentBuildingID = useAppSelector(selectCurrentBuildingID);
  const currentBuildingState = useAppSelector(selectCurrentBuildingState);
  const profileOrgId = useAppSelector(selectProfileOrganizationId);

  // State
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasBuildingListBeenDownloaded, setHasBuildingListBeenDownloaded] =
    useState(false);
  const [shouldPerformBuildingsPrefetch, setShouldPerformBuildingsPrefetch] =
    useState(false);
  const [shouldPerformCategoriesPrefetch, setShouldPerformCategoriesPrefetch] =
    useState(false);
  const [prefetchBuildingIds, setPrefetchBuildingIds] = useState<number[]>([]);

  // Hooks
  const { isTabletVersion } = useBreakpoints();

  // Job queue
  const jobQueue = useMemo(() => {
    return new JobQueue(
      isTabletVersion
        ? MOBILE_CONCURRENT_REQUEST_LIMIT
        : DESKTOP_CONCURRENT_REQUEST_LIMIT,
      isTabletVersion ? MOBILE_REQUEST_INTERVAL : DESKTOP_REQUEST_INTERVAL,
    );
  }, [isTabletVersion]);

  // Situm API
  const situmAPI = useMemo(() => {
    if (!jwt && !apikey) return;
    const props = { domain: `https://${domain}`, lang: FALLBACK_LANGUAGE };
    return jwt
      ? new SitumSDK({
          auth: {
            jwt: jwt,
          },
          ...props,
        })
      : new SitumSDK({
          auth: {
            apiKey: apikey,
          },
          ...props,
        });
  }, [apikey, domain, jwt]);

  const getCurrentAuthSession = useCallback(() => {
    if (!situmAPI) return;

    return situmAPI.authSession;
  }, [situmAPI]);

  const getJWT: SitumAPIContextType["getJWT"] = useCallback(() => {
    if (!situmAPI || !isAuthenticated) return;
    return situmAPI.jwt;
  }, [isAuthenticated, situmAPI]);

  // Expose the canonical JWT getter to the plain eventEmmiter util
  // so the gateway analytics mode can authenticate POSTs with the SDK JWT.
  useEffect(() => {
    setEventEmitterJWTGetter(getJWT ?? null);
    return () => setEventEmitterJWTGetter(null);
  }, [getJWT]);

  // Obtains last modified header value
  const getBuildingLastModified = useCallback(
    async (buildingID: number) => {
      return new Promise<number>((resolve, reject) => {
        fetch(
          `https://${API_SERVER_DOMAIN}/api/v1/buildings/${buildingID}?view=compact`,
          {
            headers: {
              authorization: `Bearer ${getJWT()}`,
            },
            method: "HEAD",
          },
        )
          .then((headResponse) => {
            const lastModified = headResponse.headers.get(
              LAST_MODIFIED_HEADER_NAME,
            );
            resolve(
              lastModified !== null && lastModified !== undefined
                ? dateToTimestamp(new Date(lastModified))
                : null,
            );
          })
          .catch(() => {
            reject();
          });
      });
    },
    [getJWT],
  );

  /**
   * Checks if the complete information about a building is already stored
   */
  const isBuildingInStore = useCallback(
    ({ id }: { id: number; force?: boolean }) => {
      if (!situmAPI) return;

      return (
        storeBuildings[id] && storeBuildings[id].state !== BuildingState.PARTIAL
      );
    },
    [situmAPI, storeBuildings],
  );

  /**
   * Fetches information abount a building given its id, then it maps
   * its data to the @Building object. All of this is
   * done asynchronously
   */
  const getBuildingById: SitumAPIContextType["getBuildingById"] = useCallback(
    ({ id, force = false }) => {
      if (!situmAPI) return;

      // Avoid fetching same building twice
      if (!force && isBuildingInStore({ id })) {
        return new Promise((resolve, _) => resolve(storeBuildings[id].value));
      }

      return jobQueue.enqueueUnique(id.toString(), () =>
        situmAPI.cartography
          .getBuildingById(id, { view: "compact" })
          .then(buildingMapper),
      );
    },
    [situmAPI, storeBuildings],
  );

  /**
   * Fetches all pois belonging to the building associated to the given id
   */
  const getPoisOnBuilding: SitumAPIContextType["getPoisOnBuilding"] =
    useCallback(
      async ({ id }) => {
        if (!situmAPI) return;

        // Avoid fetching the same info
        if (isBuildingInStore({ id })) {
          return new Promise((resolve, _) =>
            resolve(storeBuildings[id].value.pois),
          );
        }
        const pois = await situmAPI.cartography.getPois({ buildingId: id });

        return poiMapper(
          pois,
          storeBuildings[id].value.coordinateConverterFlatEarth,
        );
      },
      [situmAPI, storeBuildings],
    );

  /**
   * Updates the pois stored on the building
   */
  const setPoisOnBuilding: SitumAPIContextType["setPoisOnBuilding"] =
    useCallback(
      async ({ id, pois }) => {
        if (!situmAPI) return;

        if (storeBuildings[id]) {
          storeBuildings[id].value.pois = pois;
        }
      },
      [situmAPI, storeBuildings],
    );

  /**
   * Obtains the list with miniminal information of all the buildings
   * on an organization
   */
  const getBuildings: SitumAPIContextType["getBuildings"] = useCallback(() => {
    if (!situmAPI) return;
    return situmAPI.cartography.getBuildings({ view: "compact" });
  }, [situmAPI]);

  /**
   * Obtains the list of all poi categories on an organization
   */
  const getPoiCategories: SitumAPIContextType["getPoiCategories"] =
    useCallback(() => {
      if (!situmAPI) return;
      return situmAPI.cartography.getPoiCategories();
    }, [situmAPI]);

  /**
   * Obtains information about the current organization
   */
  const getCurrentOrganization: SitumAPIContextType["getCurrentOrganization"] =
    useCallback(() => {
      if (!situmAPI) return;
      return situmAPI.cartography.getCurrentOrganization();
    }, [situmAPI]);

  // TODO add this heavily hardcoded functions to SitumSDK JS
  const getCalibrationData: SitumAPIContextType["getCalibrationData"] = (
    floorId,
    type,
    signal,
  ) => {
    const url = `https://dashboard.situm.com/api/v1/calibrations/scans?type=${type}&floor_id=${floorId}`;
    return fetch(url, {
      headers: {
        authorization: `Bearer ${getJWT()}`,
      },
      signal: signal,
    }).then((response) => response.json()) as Promise<ModelScans>;
  };

  const getModelConfig: SitumAPIContextType["getModelConfig"] = (
    buildingId,
  ) => {
    const url = `https://dashboard.situm.com/api/v1/calibrations/model_config/${buildingId}`;
    return fetch(url, {
      headers: {
        authorization: `Bearer ${getJWT()}`,
      },
    }).then((response) => response.json()) as Promise<ModelConfig>;
  };

  const putPaths: SitumAPIContextType["putPaths"] = async (
    buildingId,
    paths,
  ) => {
    await situmAPI.cartography.patchPath(buildingId, paths);
  };

  const uploadGeoJSON: SitumAPIContextType["uploadGeoJSON"] = useCallback(
    async ({ buildingID, geojson }) => {
      if (!situmAPI) return;

      return situmAPI.cartography.uploadGeoJSON({
        buildingId: buildingID,
        geojson: geojson.getGeoJSON(),
      });
    },
    [situmAPI],
  );

  const deleteGeoJSON: SitumAPIContextType["deleteGeoJSON"] = useCallback(
    async (buildingID) => {
      if (!situmAPI) return;

      return situmAPI.cartography.deleteGeoJSON(buildingID);
    },
    [situmAPI],
  );

  const uploadGeoJSONTheme: SitumAPIContextType["uploadGeoJSONTheme"] =
    useCallback(
      async ({ buildingID, geojsonTheme }) => {
        if (!situmAPI) return;

        return situmAPI.cartography.uploadGeoJSONTheme({
          buildingId: buildingID,
          theme: geojsonTheme.theme,
        });
      },
      [situmAPI],
    );

  const deleteShareLocationSession: SitumAPIContextType["deleteShareLocationSession"] =
    useCallback(
      async ({ id, deviceId }) => {
        if (!situmAPI) throw new Error("Not initialized");
        const jwtStr = getJWT?.();
        if (!jwtStr) throw new Error("No JWT available");

        const url = `https://${domain}/api/v1/${SHARED_LOCATION_ENDPOINT}/${id}/delete`;

        const response = await fetch(url, {
          body: JSON.stringify({
            device_id: deviceId,
          }),
          headers: {
            Authorization: `Bearer ${jwtStr}`,
            "Content-Type": "application/json",
          },
          method: "POST",
        });

        if (!response.ok) {
          throw new Error(
            `Failed to delete share location session (${response.status})`,
          );
        }
      },
      [domain, getJWT, situmAPI],
    );

  const createShareLocationSession: SitumAPIContextType["createShareLocationSession"] =
    useCallback(
      async ({ deviceId, durationMinutes, signal }) => {
        if (!situmAPI) throw new Error("Not initialized");
        const jwtStr = getJWT?.();
        if (!jwtStr) throw new Error("No JWT available");

        const url = `https://${domain}/api/v1/${SHARED_LOCATION_ENDPOINT}`;
        const performCreate = async () => {
          return await fetch(url, {
            body: JSON.stringify({
              device_id: deviceId,
              duration_minutes: durationMinutes,
            }),
            headers: {
              Authorization: `Bearer ${jwtStr}`,
              "Content-Type": "application/json",
            },
            method: "POST",
            signal,
          });
        };

        const response = await performCreate();

        if (!response.ok) {
          let errorInfo = {};
          try {
            errorInfo = await response.json();
          } catch {
            errorInfo = { message: response.statusText };
          }
          throw errorInfo;
        }
        // libs
        return (await response.json()) as ShareLocationSession;
      },
      [domain, getJWT, situmAPI],
    );

  const getSharedLocationById: SitumAPIContextType["getSharedLocationById"] =
    useCallback(
      async ({ locationSessionId }) => {
        if (!situmAPI) throw new Error("Not initialized");
        const jwtStr = getJWT?.();
        if (!jwtStr) throw new Error("No JWT available");

        const url = `https://${domain}/api/v1/${SHARED_LOCATION_ENDPOINT}/${locationSessionId}`;

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${jwtStr}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          if (response.status === 409) {
            return null;
          }
          throw new Error(
            `Failed to fetch shared location (${response.status})`,
          );
        }

        const data = await response.json();

        return mapSharedLocationSession(data);
      },
      [domain, situmAPI, getJWT],
    );

  const getActiveShareLocationSessionByDeviceId: SitumAPIContextType["getActiveShareLocationSessionByDeviceId"] =
    useCallback(
      async ({ deviceId }) => {
        if (!situmAPI) throw new Error("Not initialized");
        const jwtStr = getJWT?.();
        if (!jwtStr) throw new Error("No JWT available");

        const url = `https://${domain}/api/v1/${SHARED_LOCATION_ENDPOINT}/device/${deviceId}/active`;

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${jwtStr}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(
            `Failed to fetch active shared location (${response.status})`,
          );
        }

        const data = await response.json();

        return mapActiveShareLocationResponse(data);
      },
      [domain, situmAPI, getJWT],
    );

  /**
   * Fetches flight information from the Situm API.
   */
  const getFlightsInformation: SitumAPIContextType["getFlightsInformation"] =
    useCallback(async () => {
      if (!situmAPI) throw new Error("Not initialized");
      const jwtStr = getJWT?.();
      if (!jwtStr) throw new Error("No JWT available");

      const url = `https://${domain}/api/v1/flights`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${jwtStr}`,
          "Content-Type": "application/json",
        },
        method: "GET",
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch flight information (${response.status})`,
        );
      }

      const json = await response.json();
      return json as SitumFlightInformation[];
    }, [domain, situmAPI, getJWT]);

  const getBuildingList = useCallback(async () => {
    const buildings = await getBuildings();
    if (!buildings || buildings.length === 0) {
      return null;
    }
    const buildingsMapWithFloor = new Map<number, any>();
    buildings
      .filter((b) => {
        const building = b as any;
        return building.floors && building.floors.length !== 0;
      })
      .forEach((b) => {
        buildingsMapWithFloor.set(b.id, b);
      });

    if (buildingsMapWithFloor.size === 0) {
      return null;
    }

    return buildingsMapWithFloor;
  }, [getBuildings]);

  const getFullBuildingsList = useCallback(async () => {
    if (!getBuildings) return;
    const buildings = await getBuildings();
    if (!buildings) return;
    const buildingsParsed = await Promise.all(
      buildings.map(async (b) => {
        const building = buildingMapper(b);
        if (building != null && building !== undefined) {
          let completeBuilding = await getBuildingById({
            force: true,
            id: building.id,
          });
          completeBuilding = await loadIMDf(completeBuilding);
          return completeBuilding;
        }
      }),
    );
    if (!buildingsParsed || buildingsParsed.length === 0) {
      return null;
    }

    return buildingsParsed.filter((b): b is Building => b !== undefined);
  }, [getBuildings, getBuildingById]);

  /**
   * Loads information for a given building
   */
  const loadBuilding = useCallback(
    async (building: Building, force = false) => {
      if (!getBuildingById) return;
      try {
        // Load first building
        getBuildingById({ force: force, id: building.id })!.then(
          async (building) => {
            if (!building) return;
            getStyleFromCF(building);
            loadIMDf(building).then((b) => {
              setBuildingAction(b);
            });
          },
        );
      } catch (e) {
        console.error("Loading building", { e });
        dispatch(setError(e));
      }
    },
    [getBuildingById],
  );

  const syncLoadBuilding = useCallback(
    async ({ id }: { id: Building["id"] }) => {
      if (!getBuildingById) return;
      try {
        // Load first building
        let b = await getBuildingById({ id });
        if (!b) return;
        getStyleFromCF(b);
        b = await loadIMDf(b);
        setBuildingAction(b);
        return b;
      } catch (e) {
        console.error("Loading building", { e });
        dispatch(setError(e));
      }
    },
    [getBuildingById],
  );

  /**
   * Checks for organization ID mismatch between the loaded profile and the authenticated organization.
   * If a mismatch is detected (e.g., using an API key for Org A with a profile from Org B),
   * we clean up the state and reload the configuration without the profile.
   */
  const handleOrganizationMismatch = useCallback(
    async (profileName: string, organizationId: string) => {
      const searchParams = new URLSearchParams(window.location.search);

      if (
        profileName &&
        searchParams.get("apikey") &&
        profileOrgId &&
        profileOrgId !== organizationId
      ) {
        // Refetch config without profile
        removeProfileFromUrlWithReload();
        return true;
      }
      return false;
    },
    [profileOrgId],
  );

  /**
   * Downloads minimal information about the buildings, categories and
   * organization to show UI. If an organization mismatch is detected between
   * the provided API key and the profile, it forces a refetch without the profile.
   */
  const initApp = useCallback(
    async (profileName: string) => {
      try {
        // Authenticate
        const authSession = await getCurrentAuthSession();
        if (!authSession) {
          return;
        }
        dispatch(setApiPermissionLevel(authSession.apiPermissionLevel));
        const organization = await getCurrentOrganization();

        if (await handleOrganizationMismatch(profileName, organization.id)) {
          return initApp("");
        }

        setIsAuthenticated(true);
        dispatch(setError(null));

        setTag("organization_id", organization.id);
        dispatch(setCurrentOrganization(organizationMapper(organization)));
        // Start all the promises in parallel
        const buildingsPromise = getBuildingList();
        const categoriesPromise = getPoiCategories();
        const favoritePoiCategoryPromise = createFavoritePoiCategory();
        const findMyCarCat = PoiCategory.fromFindMyCar();

        // Wait for buildings and categories to load
        const [buildingsOnlyWithFloors, categories] = await Promise.all([
          buildingsPromise,
          categoriesPromise,
        ]);

        if (!buildingsOnlyWithFloors) return;

        // clear buildings in case manipulating session with jwt externally
        dispatch(setBuildings([]));

        // Set up initial building
        const initialBuilding = getInitialBuilding(buildingsOnlyWithFloors);
        dispatch(setCurrentBuildingID(initialBuilding.id));
        emmitEventAppStarted(initialBuilding.id, profileName);
        setBuildingAction(initialBuilding, BuildingState.PARTIAL);

        // Set POI categories
        const poiCategories = poiCategoryFactory(categories);
        poiCategories.unshift(await favoritePoiCategoryPromise);
        poiCategories.unshift(findMyCarCat);
        dispatch(setPoiCategories(poiCategories));

        // Prefetch the rest of the buildings (excluding the initial one)
        const allBuildings = Array.from(buildingsOnlyWithFloors.values())
          .filter((b) => b.id !== initialBuilding.id)
          .map(applyBuildingMapper);

        if (allBuildings.length === 0) {
          setHasBuildingListBeenDownloaded(true);
        } else {
          appendBuildingsAction(
            allBuildings.filter(Boolean),
            BuildingState.PARTIAL,
          );
          setHasBuildingListBeenDownloaded(true);
        }
      } catch (e) {
        console.error("Initializing app", { e });
        dispatch(setError(e));
      }
    },
    [
      getCurrentOrganization,
      getBuildingList,
      getPoiCategories,
      createFavoritePoiCategory,
      getInitialBuilding,
      handleOrganizationMismatch,
    ],
  );

  /**
   * Downloads information about all buildings again
   * @returns
   */
  const refreshData = useCallback(
    async ({
      searchParams,
      profileUrl,
      profileDomain,
    }: {
      searchParams: any;
      profileUrl: string | null;
      profileDomain: string | null;
    }) => {
      if (
        !getCurrentOrganization ||
        !getPoiCategories ||
        !getFullBuildingsList
      ) {
        return;
      }
      if (!isOnline) {
        console.error("Error refreshing app data: device is offline");
        return;
      }

      // await for remote profile info to update
      await loadConfig(searchParams, profileUrl, profileDomain);

      try {
        // Load organization info (needs await else several auth requests are fired at once)
        await getCurrentOrganization()!.then((org) => {
          setTag("organization_id", org.id);
          dispatch(setCurrentOrganization(organizationMapper(org)));
        });

        //Buildings
        const buildings = await getFullBuildingsList();

        if (!buildings) return;
        setBuildingsAction(buildings, BuildingState.COMPLETE);

        //Categories
        const categories = await getPoiCategories();
        const poiCategories = poiCategoryFactory(categories);
        poiCategories && dispatch(setPoiCategories(poiCategories));
      } catch (e) {
        console.error("Error refreshing app data", { e });
        dispatch(setError(e));
      }
    },
    [isOnline, getCurrentOrganization, getPoiCategories, getFullBuildingsList],
  );

  // --------------------- Prefetch handling
  const getPrefetchedBuildingsFromStorage = () => {
    const buildingIds = safeLocalStorage.getItem("prefetch-building-ids");
    return buildingIds && JSON.parse(buildingIds || "")
      ? (JSON.parse(buildingIds || "") as Array<number>)
      : [];
  };

  useEffect(() => {
    setPrefetchBuildingIds(getPrefetchedBuildingsFromStorage());
  }, []);

  useEffect(() => {
    safeLocalStorage.setItem("prefetch-building-ids", prefetchBuildingIds);
  }, [prefetchBuildingIds]);

  const addPrefetchBuildingId = useCallback((id: number) => {
    setPrefetchBuildingIds((p) => {
      if (p.includes(id)) return p;
      p.push(id);
      return [...p];
    });
  }, []);

  const isBuildingPrefetched = useCallback(
    (buildingId: number) => {
      if (prefetchBuildingIds.includes(buildingId)) {
        return true;
      }
      return getPrefetchedBuildingsFromStorage().includes(buildingId);
    },
    [prefetchBuildingIds],
  );

  // Only perform prefetch after map has been loaded
  const shouldPerformPrefetch =
    situmAPI &&
    mapStyleLoaded &&
    prefetchData !== PrefetchMode.NONE &&
    isOnline;

  /*
    In order to perform the buildings prefetch we have to wait for
      - The partial download of the list of buildings
      - The complete download of the current building
    */
  useEffect(() => {
    setShouldPerformBuildingsPrefetch(
      shouldPerformPrefetch &&
        hasBuildingListBeenDownloaded &&
        currentBuildingState === BuildingState.COMPLETE,
    );
  }, [
    hasBuildingListBeenDownloaded,
    shouldPerformPrefetch,
    currentBuildingState,
  ]);

  useEffect(() => {
    setShouldPerformCategoriesPrefetch(
      shouldPerformPrefetch && currentBuildingState === BuildingState.COMPLETE,
    );
  }, [shouldPerformPrefetch, currentBuildingState]);

  const buildingsToPrefetch = useMemo(() => {
    if (!shouldPerformBuildingsPrefetch) return;
    // Only prefetch current building
    if (prefetchData === PrefetchMode.MINIMAL) {
      if (
        !currentBuildingID ||
        currentBuildingState === BuildingState.PREFETCHED
      ) {
        return;
      }
      return [storeBuildings[currentBuildingID].value];
    }
    return Object.values(storeBuildings)
      .filter((b: BuildingElement) => b.state !== BuildingState.PREFETCHED)
      .map((b) => b.value);
  }, [shouldPerformBuildingsPrefetch, currentBuildingID]);

  const poiCategoriesToPrefetch = useMemo(() => {
    if (!shouldPerformCategoriesPrefetch) return;
    return poiCategories;
  }, [poiCategories, shouldPerformCategoriesPrefetch]);

  const fetchAllBuildings = async () => {
    const buildings = await getFullBuildingsList();
    const a = Object.values(buildings)
      .map((b) => b.id)
      .map((bId) => {
        return getBuildingById({ id: bId });
      });
    Promise.all(a).then((resolvedBuildings) => {
      appendBuildingsAction(resolvedBuildings, BuildingState.COMPLETE);
    });
  };

  const hasCurrentBuildingDataChanged = useCallback(async () => {
    return new Promise<void>((resolve, reject) => {
      getBuildingLastModified(currentBuildingID)
        .then(async (lastModified) => {
          // Reads building entry from cache, if it fails or it does not exist it will asume building
          // data has changed
          try {
            caches
              .open(CARTOGRAPHY_CACHE_NAME)
              .then((cartographyCache) => {
                cartographyCache
                  .match(
                    `https://${API_SERVER_DOMAIN}/api/v1/buildings/${currentBuildingID}?view=compact`,
                  )
                  .then((cachedResponse) => {
                    const cachedAtTimestamp = Number(
                      cachedResponse.headers.get(CACHED_AT_HEADER_NAME),
                    );
                    if (
                      !lastModified ||
                      // Checks if it was modifed after it was last cached
                      lastModified > cachedAtTimestamp
                    ) {
                      resolve();
                    } else {
                      reject();
                    }
                  })
                  .catch(() => {
                    resolve();
                  });
              })
              .catch(() => {
                resolve();
              });
          } catch {
            resolve();
          }
        })
        .catch(() => {
          resolve();
        });
    });
  }, [currentBuildingID, getBuildingLastModified]);

  const uploadMapStyle = useCallback(
    async (file: File) => {
      if (!situmAPI) throw new Error("Not initialized");
      if (!getJWT) throw new Error("No JWT available");
      const url = `https://${domain}/api/v1/mapstyles`;
      const form = new FormData();
      form.append("styleFile", file);
      const resp = await fetch(url, {
        body: form,
        headers: { Authorization: `Bearer ${getJWT()}` },
        method: "POST",
      });
      if (!resp.ok) {
        const msg = await resp.text().catch(() => "");
        throw new Error(`Upload failed (${resp.status}) ${msg}`.trim());
      }
      return (await resp.json()) as { name: string; url: string };
    },
    [domain, getJWT, situmAPI],
  );

  const uploadIconImage = useCallback(
    async (file: File) => {
      if (!situmAPI) throw new Error("Not initialized");
      if (!getJWT) throw new Error("No JWT available");
      const url = `https://${domain}/api/v1/images`;
      const form = new FormData();
      form.append("image", file);
      form.append("purpose", "icon");
      const resp = await fetch(url, {
        body: form,
        headers: { Authorization: `Bearer ${getJWT()}` },
        method: "POST",
      });
      if (!resp.ok) {
        let msg = "";
        try {
          const contentType = resp.headers.get("content-type") || "";
          if (contentType.includes("application/json")) {
            const data: any = await resp.json();
            msg = data?.message ?? "";
          } else {
            msg = await resp.text();
          }
        } catch (_) {
          /* ignore parse errors */
        }
        throw new Error(`Upload failed (${resp.status}) ${msg}`.trim());
      }
      return (await resp.json()) as { id: string | number; url: string };
    },
    [domain, getJWT, situmAPI],
  );

  return (
    <SitumAPIContext.Provider
      value={{
        // actions
        addPrefetchBuildingId,
        // states
        buildingsToPrefetch,
        createShareLocationSession,
        deleteGeoJSON,
        deleteShareLocationSession,
        fetchAllBuildings,
        getActiveShareLocationSessionByDeviceId,

        // Getters
        getBuildingById,
        getBuildings,
        getCalibrationData,
        getCurrentOrganization,
        getFlightsInformation,
        getJWT,
        getModelConfig,
        getPoiCategories,
        getPoisOnBuilding,
        getSharedLocationById,
        hasCurrentBuildingDataChanged,
        initApp,
        isAuthenticated,
        isBuildingPrefetched,
        jobQueue,
        loadBuilding,
        poiCategoriesToPrefetch,
        prefetchBuildingIds,
        putPaths,
        refreshData,
        setPoisOnBuilding,
        setPrefetchBuildingIds,
        situmAPI,
        syncLoadBuilding,
        uploadGeoJSON,
        uploadGeoJSONTheme,
        uploadIconImage,
        uploadMapStyle,
      }}
    >
      {children}
    </SitumAPIContext.Provider>
  );
};
